/**
 * The pieces the shareable completion cards (course and module) draw with:
 * the redesign tokens as literal colours (an SVG exported to an image cannot
 * read CSS variables), the two fonts, and the shared marks (sparkles, the
 * trophy medallion, a stat tile, the plan badge and brand footer).
 *
 * The new skin is variant A of the course card (David 2026-10-24, #1078);
 * the module card took it on 2026-10-25 so the two read as one family.
 */

const NAVY = '#142033';

/**
 * The redesign tokens the cards use (design-system/tokens.css), as literals:
 * the LIGHT skin, which the share image always uses (a PNG cannot read CSS
 * variables, and a shared image stays light, like an email). On screen in
 * dark the card draws with liveSkin() instead (dark mode, David 2026-10-27:
 * "The module complete (and other congrats modals) are still light").
 */
export const LIGHT_SKIN = {
  canvas: '#fffaf5', // --ee-canvas
  surface: '#ffffff', // --ee-surface
  warm: '#fff4e8', // --ee-surface-warm
  muted: '#f7f2ec', // --ee-surface-muted
  navy: NAVY, // --ee-navy
  ink2: '#4a5361', // --ee-segment-ink (6.74:1 on the track, 8.0:1 on white)
  secondary: '#66717e', // --ee-text-secondary
  orange: '#f47a20', // --ee-orange (fills only, never text)
  orangeLight: '#ffa056', // --ee-circle-start
  orangeText: '#b4510e', // --ee-orange-text
  peach: '#ffe9d6', // --ee-peach
  gold: '#f2b84d', // --ee-gold
  goldDeep: '#d99a2b',
  goldSoft: '#fdf1d8', // --ee-gold-soft
  goldText: '#8a5a0b', // --ee-gold-text
  shadow: NAVY, // the surface's drop shadow (--ee-veil: navy light, black dark)
};
export type Skin = { [K in keyof typeof LIGHT_SKIN]: string };

/**
 * Where each skin colour comes from in the live theme. goldDeep (the
 * trophy's base) has no token: a saturated trophy colour, the same in both.
 */
export const SKIN_TOKENS: { [K in keyof Skin]?: string } = {
  canvas: '--ee-canvas',
  surface: '--ee-surface',
  warm: '--ee-surface-warm',
  muted: '--ee-surface-muted',
  navy: '--ee-navy',
  ink2: '--ee-segment-ink',
  secondary: '--ee-text-secondary',
  orange: '--ee-orange',
  orangeLight: '--ee-circle-start',
  orangeText: '--ee-orange-text',
  peach: '--ee-peach',
  gold: '--ee-gold',
  goldSoft: '--ee-gold-soft',
  goldText: '--ee-gold-text',
  shadow: '--ee-veil',
};

/**
 * The skin as the page's theme paints it: each colour read from its token on
 * the document (getComputedStyle resolves var() chains), the light literal
 * where a token reads empty (no stylesheet, e.g. a unit test).
 */
export function liveSkin(root: Element = document.documentElement): Skin {
  const style = getComputedStyle(root);
  const out = { ...LIGHT_SKIN };
  for (const [key, token] of Object.entries(SKIN_TOKENS) as [keyof Skin, string][]) {
    const value = style.getPropertyValue(token).trim();
    if (value) out[key] = value;
  }
  return out;
}

export const HEAD_FONT = "'Space Grotesk', 'Inter', system-ui, sans-serif";
export const BODY_FONT = "'Inter', system-ui, sans-serif";

/** The graduated giraffe both cards show; Share inlines it into the export. */
export const GIRAFFE_SRC = '/giraffe_graduated.webp';

// Escape XML special characters
export function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** Wrap text into lines of at most maxCharsPerLine (a longer word stays whole). */
export function wrapText(text: string, maxCharsPerLine: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (testLine.length <= maxCharsPerLine) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
      }
      currentLine = word;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
}

export function sparkle(
  x: number,
  y: number,
  r: number,
  opacity: number,
  p: Skin = LIGHT_SKIN,
): string {
  return `<path transform="translate(${x} ${y})" d="M0 ${-r} Q0 0 ${r} 0 Q0 0 0 ${r} Q0 0 ${-r} 0 Q0 0 0 ${-r} Z" fill="${p.gold}" opacity="${opacity}"/>`;
}

/** The calmer sparkles around the trophy, the same four on both cards. */
export function headerSparkles(p: Skin = LIGHT_SKIN): string {
  return `${sparkle(380, 70, 16, 0.55, p)}${sparkle(708, 128, 12, 0.45, p)}${sparkle(330, 150, 9, 0.35, p)}${sparkle(750, 52, 8, 0.35, p)}`;
}

// Line icons for the stat tiles (emoji differ per platform and in the export).
export const ICONS = {
  book: '<path d="M-14 -11 Q-7 -14 0 -9 Q7 -14 14 -11 V11 Q7 8 0 13 Q-7 8 -14 11 Z M0 -9 V13" fill="none" stroke-width="3.2" stroke-linejoin="round"/>',
  star: '<path d="M0 -14 L4.2 -4.6 L14 -4 L6.6 2.6 L8.8 12.6 L0 7.4 L-8.8 12.6 L-6.6 2.6 L-14 -4 L-4.2 -4.6 Z" fill="none" stroke-width="3.2" stroke-linejoin="round"/>',
  calendar:
    '<rect x="-13" y="-10" width="26" height="23" rx="4" fill="none" stroke-width="3.2"/><path d="M-13 -2 H13 M-6 -15 V-6 M6 -15 V-6" stroke-width="3.2" stroke-linecap="round"/>',
  // A winding path between two stops: the units (paths) of a module.
  route:
    '<circle cx="-9" cy="-10" r="4.5" fill="none" stroke-width="3.2"/><circle cx="9" cy="10" r="4.5" fill="none" stroke-width="3.2"/><path d="M-4.5 -10 H6 Q12 -10 12 -4 Q12 2 6 2 H-6 Q-12 2 -12 7 Q-12 10 -9 10 H4.5" fill="none" stroke-width="3.2" stroke-linecap="round"/>',
  // Three rising bars: the average score.
  chart:
    '<path d="M-12 13 V2 M0 13 V-12 M12 13 V-4" fill="none" stroke-width="4.4" stroke-linecap="round"/>',
} as const;

export const STAT_TILE = { width: 270, height: 172 } as const;

/** One stat tile: icon, value, label. `stat` names it for tests (data-stat). */
export function statTile(
  x: number,
  y: number,
  icon: keyof typeof ICONS,
  value: string,
  label: string,
  stat: string,
  p: Skin = LIGHT_SKIN,
): string {
  const w = STAT_TILE.width;
  const cx = x + w / 2;
  return `
    <g data-stat="${stat}">
    <rect x="${x}" y="${y}" width="${w}" height="${STAT_TILE.height}" rx="30" fill="${p.warm}"/>
    <g transform="translate(${cx} ${y + 40})" stroke="${p.orange}" fill="${p.orange}">${ICONS[icon]}</g>
    <text data-stat-value="" x="${cx}" y="${y + 112}" font-family="${HEAD_FONT}" font-weight="700" font-size="54" fill="${p.navy}" text-anchor="middle">${escapeXml(value)}</text>
    <text x="${cx}" y="${y + 148}" font-family="${BODY_FONT}" font-weight="500" font-size="27" fill="${p.ink2}" text-anchor="middle">${escapeXml(label)}</text>
    </g>`;
}

export function trophy(cx: number, cy: number, p: Skin = LIGHT_SKIN): string {
  return `
    <g transform="translate(${cx} ${cy})">
      <circle r="60" fill="${p.goldSoft}"/>
      <circle r="60" fill="none" stroke="${p.gold}" stroke-opacity="0.45" stroke-width="2"/>
      <path d="M-20 -22 H-31 Q-33 -2 -15 3 M20 -22 H31 Q33 -2 15 3" fill="none" stroke="${p.gold}" stroke-width="5" stroke-linecap="round"/>
      <path d="M-22 -28 H22 V-8 A22 22 0 0 1 -22 -8 Z" fill="${p.gold}"/>
      <path d="M-12 -22 V-10" stroke="#fff" stroke-opacity="0.6" stroke-width="4" stroke-linecap="round"/>
      <rect x="-4" y="13" width="8" height="10" fill="${p.gold}"/>
      <rect x="-17" y="23" width="34" height="9" rx="4" fill="${p.goldDeep}"/>
    </g>`;
}

/** The canvas, its two soft glows and the surface's drop shadow (ids prefixed per card). */
export function canvasDefs(prefix: string, p: Skin = LIGHT_SKIN): string {
  return `
      <radialGradient id="${prefix}GlowTop" cx="50%" cy="0%" r="70%">
        <stop offset="0%" stop-color="${p.peach}"/><stop offset="100%" stop-color="${p.canvas}" stop-opacity="0"/>
      </radialGradient>
      <radialGradient id="${prefix}GlowLow" cx="100%" cy="100%" r="60%">
        <stop offset="0%" stop-color="${p.goldSoft}"/><stop offset="100%" stop-color="${p.canvas}" stop-opacity="0"/>
      </radialGradient>
      <filter id="${prefix}Shadow" x="-10%" y="-10%" width="120%" height="125%">
        <feDropShadow dx="0" dy="14" stdDeviation="22" flood-color="${p.shadow}" flood-opacity="0.08"/>
      </filter>`;
}

export function canvas(prefix: string, p: Skin = LIGHT_SKIN): string {
  return `
    <rect width="1080" height="1350" fill="${p.canvas}"/>
    <rect width="1080" height="1350" fill="url(#${prefix}GlowTop)"/>
    <rect width="1080" height="1350" fill="url(#${prefix}GlowLow)"/>`;
}

/** The two-tone heading: first word navy, the rest the text orange. */
export function twoToneTitle(first: string, rest: string, y: number, p: Skin = LIGHT_SKIN): string {
  return `<text x="540" y="${y}" font-family="${HEAD_FONT}" font-weight="700" font-size="66" letter-spacing="1" text-anchor="middle"><tspan fill="${p.navy}">${escapeXml(first)} </tspan><tspan fill="${p.orangeText}">${escapeXml(rest)}</tspan></text>`;
}

/** The white surface with its hairline; data-card-surface marks it for tests. */
export function surface(
  x: number,
  y: number,
  width: number,
  height: number,
  prefix: string,
  p: Skin = LIGHT_SKIN,
): string {
  return `
    <rect data-card-surface="" x="${x}" y="${y}" width="${width}" height="${height}" rx="56" fill="${p.surface}" filter="url(#${prefix}Shadow)"/>
    <rect x="${x + 0.5}" y="${y + 0.5}" width="${width - 1}" height="${height - 1}" rx="56" fill="none" stroke="${p.navy}" stroke-opacity="0.06"/>`;
}

export function giraffe(x: number, y: number, size: number): string {
  return `<image xlink:href="${GIRAFFE_SRC}" href="${GIRAFFE_SRC}" x="${x}" y="${y}" width="${size}" height="${size}" preserveAspectRatio="xMidYMid meet" />`;
}

/** The footer rule's y: content above it must end before it. */
export const FOOTER_RULE_Y = 1238;

/** The plan badge (FREE / PAID) and the brand footer. */
export function planAndBrandFooter(isPremium: boolean, p: Skin = LIGHT_SKIN): string {
  // isPremium means "any paid plan" (Premium or Elite)
  const badgeText = isPremium ? 'PAID' : 'FREE';
  return `
    <line data-footer-rule="" x1="80" x2="1000" y1="${FOOTER_RULE_Y}" y2="${FOOTER_RULE_Y}" stroke="${p.navy}" stroke-opacity="0.08" stroke-width="2"/>
    <rect data-plan-badge-fill="" x="80" y="1262" width="118" height="48" rx="24" fill="${isPremium ? p.goldSoft : p.muted}" stroke="${p.navy}" stroke-opacity="0.14" stroke-width="2"/>
    <text data-plan-badge="" x="139" y="1295" font-family="${BODY_FONT}" font-weight="700" font-size="22" letter-spacing="3" fill="${isPremium ? p.goldText : p.ink2}" text-anchor="middle">${badgeText}</text>
    <text x="1000" y="1287" font-family="${HEAD_FONT}" font-weight="700" font-size="34" fill="${p.navy}" text-anchor="end">Empath<tspan fill="${p.orangeText}">Ease</tspan></text>
    <text x="1000" y="1318" font-family="${BODY_FONT}" font-weight="500" font-size="23" fill="${p.secondary}" text-anchor="end">https://empathease.app</text>`;
}
